import _ from 'lodash';
import { validate as uuidValidate } from 'uuid';

import { getCountFromServer } from 'firebase/firestore';
import {
  doc, getDateNow, getDoc, getDocs, query, tariffCollection, updateDoc, where, addDoc, subsidiaryCollection
} from '../config/firebase';
import CalculationMethod from '../models/enums/calculationMethod';
import { CreateOrUpdate } from '../models/interfaces/tariff';
import { titleCase } from '../utils/stringHelper';
import customerService from './customerService';
import subsidiaryService from './subsidiaryService';

export interface SyncTariffResult {
  created: number;
  updated: number;
  failed: { companyId: string | null; message: string }[];
}

async function resolveSubsidiaryId(subId: string) {
  let subsidiaryId = subId;
  if (uuidValidate(subsidiaryId)) {
    const sub = await subsidiaryService.getByIdAsync(subsidiaryId);
    const snap = await getDocs(query(subsidiaryCollection, where('cnpj', '==', sub.cnpj)));
    if (!snap.empty) {
      subsidiaryId = snap.docs[0].id;
    }
  }
  return subsidiaryId;
}

function normalizeCompanyIds(companyIds: (string | null)[]) {
  if (!companyIds.length) {
    throw new Error('Selecione ao menos um cliente.');
  }

  const hasAll = companyIds.some((id) => id === null || id === '');
  if (hasAll && companyIds.length > 1) {
    throw new Error('A opção "Todos" não pode ser combinada com clientes específicos.');
  }

  if (hasAll) return [null];

  return Array.from(new Set(companyIds.map((id) => (id === '' ? null : id))));
}

async function resolveCompanyName(customerId: string | null | undefined) {
  if (!customerId) return null;

  const customer = await customerService.getById(customerId);
  return customer?.name ?? customer?.businessName ?? null;
}

export default {
  async getAllAsync(subId: string | undefined | null) {
    let subsidiaryId = subId;
    if (subsidiaryId && uuidValidate(subsidiaryId)) {
      const newSub = await subsidiaryService.getByIdAsync(subsidiaryId);
      const subsidiariesByCNPJ = await getDocs(query(subsidiaryCollection, where('cnpj', '==', newSub.cnpj)));
      if (subsidiariesByCNPJ.size === 1) {
        subsidiaryId = subsidiariesByCNPJ.docs[0].id;
      }
    }
    let customQuery = query(tariffCollection, where('subsidiary', '!=', null));
    if (subsidiaryId) {
      customQuery = query(tariffCollection, where('subsidiary', '==', subsidiaryId));
    }

    return getDocs(customQuery).then(async (querySnapshot) => {
      let result: any[] = [];
      const promisses = querySnapshot.docs.map(async (snap) => {
        const {
          companyName, description, disabledAt, categoryDriver, calculationMethod
        } = snap.data();
        const desc = description ?? companyName;
        return {
          id: snap.id,
          description: desc,
          disabledAt: disabledAt?.toDate(),
          categoryDriver,
          calculationMethod
        };
      });

      await Promise.all(promisses).then((resultDocs) => {
        result = resultDocs;
      });

      return _.sortBy(result, [(x) => x.description.toLowerCase()]);
    });
  },

  async getById(id: string) {
    const result = await getDoc(doc(tariffCollection, id));
    if (!result.exists()) throw new Error('Tariff not found');

    const model = result.data();
    if (model?.company) {
      model.company = await customerService.getById(model.company);
    }
    return model;
  },

  async getAllCount(id: string | null | undefined) {
    const subsidiaryId = id;
    let total;
    const q = query(tariffCollection);
    const c = query(q);

    if (subsidiaryId) {
      total = await getCountFromServer(query(c, where('subsidiary', '==', subsidiaryId)));
    } else {
      total = await getCountFromServer(c);
    }

    return { total: total?.data().count };
  },

  async createAsync(model: CreateOrUpdate, subId: string, pCustomerId: string | null) {
    const subsidiaryId = await resolveSubsidiaryId(subId);
    let customerId = pCustomerId;
    if (customerId === '') customerId = null;
    this.validFields(model);

    const q = query(tariffCollection,
      where('subsidiary', '==', subsidiaryId),
      where('categoryDriver', '==', model.categoryDriver),
      where('calculationMethod', '==', model.calculationMethod),
      where('company', '==', customerId));

    const checkEqualTariff = await getDocs(q);
    if (!checkEqualTariff.empty) {
      throw new Error('Já existe uma tarifa com essa categoria.');
    }

    const companyName = await resolveCompanyName(customerId);

    const newDoc = {
      ...model,
      description: titleCase(model.description),
      subsidiary: subsidiaryId,
      company: customerId,
      companyName,
      createdAt: getDateNow()
    };

    return addDoc(tariffCollection, newDoc);
  },

  async update(model: CreateOrUpdate, id: string) {
    this.validFields(model);

    const existing = await getDoc(doc(tariffCollection, id));
    const companyId = existing.data()?.company ?? null;
    const companyName = await resolveCompanyName(companyId);

    const modelRef = doc(tariffCollection, id);
    return updateDoc(modelRef, {
      ...model,
      description: titleCase(model.description),
      companyName
    });
  },

  async syncForCompanies(
    model: CreateOrUpdate,
    subId: string,
    companyIds: (string | null)[],
    options?: { currentTariffId?: string; currentCompanyId?: string | null }
  ): Promise<SyncTariffResult> {
    this.validFields(model);
    const normalizedIds = normalizeCompanyIds(companyIds);
    const subsidiaryId = await resolveSubsidiaryId(subId);
    const result: SyncTariffResult = { created: 0, updated: 0, failed: [] };

    await Promise.all(normalizedIds.map(async (customerId) => {
      try {
        const payload: CreateOrUpdate = {
          ...model,
          fixedValues: _.cloneDeep(model.fixedValues),
          companyName: await resolveCompanyName(customerId)
        };

        const existingSnap = await getDocs(query(
          tariffCollection,
          where('subsidiary', '==', subsidiaryId),
          where('categoryDriver', '==', model.categoryDriver),
          where('calculationMethod', '==', model.calculationMethod),
          where('company', '==', customerId)
        ));

        if (!existingSnap.empty) {
          await this.update(payload, existingSnap.docs[0].id);
          result.updated += 1;
        } else if (
          options?.currentTariffId
          && (options.currentCompanyId ?? null) === (customerId ?? null)
        ) {
          await this.update(payload, options.currentTariffId);
          result.updated += 1;
        } else {
          await this.createAsync(payload, subsidiaryId, customerId);
          result.created += 1;
        }
      } catch (error) {
        result.failed.push({
          companyId: customerId,
          message: error instanceof Error ? error.message : 'Erro ao salvar tarifa.'
        });
      }
    }));

    if (result.failed.length === normalizedIds.length) {
      throw new Error(result.failed.map((item) => item.message).join(' '));
    }

    return result;
  },

  async changeStatus(id: string, isDisabled: boolean) {
    const model = await this.getById(id);
    if (!model) throw new Error('Tariff not found');

    const modelRef = doc(tariffCollection, id);
    return updateDoc(modelRef, {
      disabledAt: isDisabled ? getDateNow() : null
    });
  },

  validFields(pModel: CreateOrUpdate) {
    const model = pModel;
    if (model.calculationMethod === CalculationMethod.MileageStopped || model.calculationMethod === CalculationMethod.Ifood) {
      model.valuePerAdditionalStop = 0;
      model.valueFlag = 0;
      model.valueByKm = 0;
      model.valueByStop = 0;
      model.valueMinimum = 0;

      if (!model.fixedValues.every((x) => x.value !== undefined && x.valueBack !== undefined)) {
        throw new Error('É necessário informar o valor para todas as distâncias.');
      }
    } else {
      model.fixedValues = [];

      let hasError = false;
      if (model.valuePerAdditionalStop === undefined) hasError = true;
      else if (model.valueFlag === undefined) hasError = true;
      else if (model.valueByKm === undefined) hasError = true;
      else if (model.valueByStop === undefined) hasError = true;
      else if (model.valueMinimum === undefined) hasError = true;

      if (hasError) throw new Error('Todos os campos são obrigatórios');
    }
  }
};
